/**
 * 操作の記録 → Swift コード（labs.md「操作が Swift の 1 行として記録される」）と、
 * 再現実行の期待フレーム列（viz-protocol.md §5.1 の形）。
 */
import type { ExpectedFrame, VizState } from '../viz';
import type { LabModel, LabRun } from './types';
import { formatEntry } from './swift-format';

/** 記録欄の行（宣言 + 各操作）。そのまま貼って実行すると同じ状態になる。 */
export function recordLines<S, O>(model: LabModel<S, O>, run: LabRun<S>): string[] {
  return [...model.declarations(run.initial), ...run.steps.map((s) => formatEntry(s.entry))];
}

/** 記録欄をコピーするときの本文（末尾に改行）。 */
export function recordCode<S, O>(model: LabModel<S, O>, run: LabRun<S>): string {
  return recordLines(model, run).join('\n') + '\n';
}

/** 再現実行で viz 補助に盤面を送る行の前に置く説明（1 行目）。 */
export const REPLAY_HEADER = '// viz. で始まる行は、その時点の中身を盤面に送ります。';

export interface ReplayPlan {
  /** エディタに入れるコード。記録の各行の後に、変わった変数を盤面に送る `viz.` の行が入る。 */
  code: string;
  /** viz 補助が出すはずのフレーム列（deriveFrames と同じく、state は累積）。 */
  frames: ExpectedFrame[];
  /** フレーム i を作った記録の行（1 始まり、code の中の行番号）。 */
  frameLines: number[];
  /** フレーム i を作った記録の手（0 は初期状態、k は k 手目の後）。 */
  frameSteps: number[];
  /** 標準出力に出るはずの文字列（print の行だけ。末尾に改行）。 */
  stdout: string;
}

/** 記録から、再現実行のコードと期待フレームを作る。 */
export function replayPlan<S, O>(model: LabModel<S, O>, run: LabRun<S>): ReplayPlan {
  const lines: string[] = [REPLAY_HEADER];
  const frames: ExpectedFrame[] = [];
  const frameLines: number[] = [];
  const frameSteps: number[] = [];
  let acc: Record<string, VizState> = {};

  const show = (state: S, label: string, sourceLine: number, step: number) => {
    lines.push(model.vizLine(state, label));
    acc = { ...acc, [label]: model.snapshot(state, label) };
    frames.push({ state: acc });
    frameLines.push(sourceLine);
    frameSteps.push(step);
  };

  for (const d of model.declarations(run.initial)) lines.push(d);
  const declEnd = lines.length;
  for (const label of model.labels(run.initial)) show(run.initial, label, declEnd, 0);

  const out: string[] = [];
  run.steps.forEach((s, i) => {
    lines.push(formatEntry(s.entry));
    const at = lines.length;
    if (s.entry.stdout !== undefined) out.push(s.entry.stdout);
    for (const label of s.entry.changed) show(s.state, label, at, i + 1);
  });

  return {
    code: lines.join('\n') + '\n',
    frames,
    frameLines,
    frameSteps,
    stdout: out.length === 0 ? '' : out.join('\n') + '\n',
  };
}
